import { HomepageColumn } from "@/ui/organisms/HomepageColumn";
import { cn } from "@/utils/utils";
import type {
    THomepageBoard as THomepageBoardData,
    THomepageColumnKey,
    THomepageRow,
} from "@/store";

export interface THomepageBoardProps {
    board: THomepageBoardData;
    onRowClick?: (row: THomepageRow, columnKey: THomepageColumnKey) => void;
    /** Reveal more rows in a capped column. One handler for the whole board:
     * the page size is shared, so expanding one column expands them all. */
    onShowMore?: () => void;
    isLoadingMore?: boolean;
    className?: string;
}

/**
 * The 3-column Homepage v2 board grid — equal-width, non-draggable columns
 * (design doc §7.1). Which three columns render is entirely determined by
 * `board.columns` (the backend already returns exactly the SP or Accountant
 * set per §5.1-§5.3); this component has no view-specific logic of its own.
 */
export function HomepageBoard({
    board,
    onRowClick,
    onShowMore,
    isLoadingMore = false,
    className,
}: THomepageBoardProps) {
    return (
        <div
            data-testid="homepage-board"
            className={cn("grid grid-cols-1 gap-4 md:grid-cols-3", className)}
        >
            {board.columns.map((column) => (
                <HomepageColumn
                    key={column.key}
                    column={column}
                    view={board.view}
                    onRowClick={onRowClick}
                    onShowMore={onShowMore}
                    isLoadingMore={isLoadingMore}
                />
            ))}
        </div>
    );
}
